<template>
  <div class="group-detail">
    <h1 class="common__main-title">Move Group</h1>
    <div class="sub-title">Detail</div>
    <div class="content-wrapper">
      <ValidationObserver ref="observer">
      <div class="row mb-4">
        <div class="col">
          <div class="form-group">
            <label>Student</label>
            <div class="student-name">{{ student && student.name }}</div>
          </div>
        </div>
        <div class="col">
          <div class="form-group">
            <b-form-group label="Select a move method" v-slot="{ ariaDescribedby }">
              <b-form-radio-group
                v-model="isNewGroup"
                :aria-describedby="ariaDescribedby"
              >
                <b-form-radio
                  :aria-describedby="ariaDescribedby"
                  :value="true"
                >
                  Move to a new group
                </b-form-radio>
                <b-form-radio
                  :aria-describedby="ariaDescribedby"
                  :value="false"
                >
                  Move to an existing group
                </b-form-radio>
              </b-form-radio-group>
            </b-form-group>
          </div>
          <div v-if="!isNewGroup" class="form-group">
            <label>Destination group</label>
            <ValidationProvider rules="required" v-slot="{ errors }">
              <SelectGroupIdInput v-if="fromGroup" v-model="toGroupId"
                :fiscalYearSemester="fiscalYearSemester" :university="fromGroup.university" />
                <div class="error-text">{{ errors[0] }}</div>
            </ValidationProvider>
          </div>
          <div class="form-group">
            <label>SPIR Applicatioin ID</label>
            <input class="form-control" type="text" v-model="spirId" />
          </div>
        </div>
      </div>
      <div class="main-wrapper">
        <div class="group-content">
          <font-awesome-icon class="arrow" icon="arrow-right"/>
          <GroupView v-model="fromGroup" />
        </div>
        <div class="group-content">
          <InputForm v-if="isNewGroup" v-model="fromGroup" ref="inputForm" />
          <GroupView v-if="!isNewGroup" v-model="toGroup" />
        </div>
      </div>

      <div class="d-flex justify-content-between">
        <b-button pill variant="secondary" :to="{ name: 'GroupEdit', params: {
          groupId: this.$route.params.groupId,
        } }">
          Back
        </b-button>
        <b-button pill variant="primary" @click="confirm">Confirm</b-button>
      </div>
      </ValidationObserver>
    </div>

    <Loading :isLoading="isLoading" />
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  Group,
  MoveStudentGroupParams,
} from '@/store/groups/types'
import { FiscalYearSemester, weekdayKey, Weekdays } from '@/store/common.types'
import Loading from '@/components/parts/modal/Loading.vue'
import SelectGroupIdInput from '@/components/parts/inputItem/SelectGroupIdInput.vue'
import { Student } from '@/store/students/types'
import InputForm from '@/components/parts/group/studentMove/InputForm.vue'
import GroupView from '@/components/parts/group/studentMove/GroupView.vue'

export default Vue.extend({
  components: {
    GroupView,
    InputForm,
    ValidationProvider,
    ValidationObserver,
    Loading,
    SelectGroupIdInput,
  },

  data: (): {
    isLoading: boolean,
    toGroupId: string|undefined,
    spirId: string|undefined,
    isNewGroup: boolean,
  } => ({
    isLoading: false,
    toGroupId: undefined,
    spirId: undefined,
    isNewGroup: true,
  }),
  computed: {
    fromGroup(): Group|undefined {
      return this.$store.getters.findGroupById(this.$route.params.groupId)
    },
    student(): Student|undefined {
      return this.$store.getters.findStudentById(this.$route.params.studentId)
    },
    toGroup(): Group|undefined {
      if (this.isNewGroup) {
        return (this.$refs.inputForm as InputForm).input()
      }
      return this.$store.getters.findGroupById(this.toGroupId)
    },
    fiscalYearSemester(): FiscalYearSemester|undefined {
      if (!this.fromGroup) {
        return undefined
      }
      return {
        fiscalYear: this.fromGroup.fiscalYear,
        semester: this.fromGroup.semester,
      }
    },
  },

  created() {
    this.isLoading = true
    Promise.all([
      this.$store.dispatch('getGroupById', { groupId: this.$route.params.groupId }),
      this.$store.dispatch('getStudentById', { studentId: this.$route.params.studentId }),
    ]).then(() => {
      this.isLoading = false
    })
  },

  watch: {
    toGroupId(groupId: string) {
      this.$store.dispatch('getGroupById', { groupId })
    },
  },

  methods: {
    async confirm() {
      const isOk = await (this.$refs.observer as any).validate()
      if (!isOk) {
        return
      }

      const confirmed = await this.$bvModal.msgBoxConfirm("It's OK?")
      if (!confirmed === true) {
        return
      }

      try {
        this.isLoading = true
        const groupId: string = this.isNewGroup
          ? await this.moveStudentNewGroup()
          : await this.moveStudentAnotherGroup()
        this.isLoading = false
        await this.$bvModal.msgBoxOk('Success!')
        this.$router.push({ name: 'GroupEdit', params: { groupId } })
      } catch (error: any) {
        this.isLoading = false
        const message = error?.response?.data?.message || error.message
        this.$bvModal.msgBoxOk(`Failed : ${message}`)
      }
    },
    async moveStudentAnotherGroup(): Promise<string> {
      const params: MoveStudentGroupParams = {
        student: this.student as Student,
        fromGroup: this.fromGroup as Group,
        toGroup: this.toGroup as Group,
        spirId: this.spirId || undefined,
      }
      await this.$store.dispatch('moveStudentAnotherGroup', params)
      return (this.toGroup as Group).id
    },
    async moveStudentNewGroup(): Promise<string> {
      const params: MoveStudentGroupParams = {
        student: this.student as Student,
        fromGroup: this.fromGroup as Group,
        toGroup: this.toGroup as Group,
        spirId: this.spirId || undefined,
      }
      await this.$store.dispatch('moveStudentNewGroup', params)
      return this.$store.state.groups.createdId
    },
  },
})
</script>

<style scoped>
.sub-title {
  padding:10px;
  margin-bottom: 20px;
  text-align: center;
  background: #D8D8D8;
}

.content-wrapper {
  background: #D8D8D8;
  padding: 20px 50px;
}

.group-content {
  position: relative;
  background: #fff;
  padding: 20px;
  border-radius: 20px;
  margin-bottom:20px;
  width: 40%;
}

table.detail-table {
  width:100%;
}

table.detail-table th {
  display: block;
  text-align: left;
  margin:10px 0;
}

table.detail-table td {
  display: block;
  margin:10px 0;
}

.student {
  margin-bottom:10px;
  align-items: center;
  display: flex;
}

.student-name {
  margin-top:10px;
  margin-left: 10px;
}

.main-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.arrow {
  position: absolute;
  color:#1cc8a0;
  width: 100px;
  height: 100px;
  font-size: 100px;
  right:-70px;
  top:0;
  bottom:0;
  margin: auto 0;
}
</style>
<style>
fieldset legend.col-form-label {
  font-weight: bold;
}
fieldset label.custom-control-label {
  font-weight: normal;
}
</style>
